import {
  Badge,
  FlexBox,
  Page,
  Pagination,
  Spinner,
} from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';
import {
  ReadAdvancedSearchResultsApiArg,
  SearchFilterCondition,
  User,
} from 'lib-shared-types-api';
import {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { conversationsApi } from 'apis/conversations';
import { presenceApi } from 'apis/presence';
import { searchApi } from 'apis/search';
import { actions as personalActions } from 'components/settings/actions/personal';
import { userModalActions } from 'components/usersModal';
import { twilioActions } from 'middleware/twilio/actions/twilioActions';
import { websocketActions } from 'middleware/websocket/actions/websocket';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';

import { useMemberEventsCacheSync } from '../hooks/useMemberEventsCacheSync';
import { useScrollToTopOnPageChange } from '../hooks/useScrollToTopOnPageChange';

import {
  type ActiveFilters,
  hasActiveFilters,
  isValidStatus,
  SearchControls,
  type Status,
} from './searchControls';
import styles from './teamPage.module.scss';
import { TeamTableEmptyState } from './teamTableEmptyState';
import { TeamTableLoading } from './teamTableLoading';
import { TeamTableRow } from './teamTableRow';

type AvailableFilters = {
  status: { [K in Status]: SearchFilterCondition[] };
  team: (teamIds: string[]) => SearchFilterCondition[];
  agent: (agentIds: string[]) => SearchFilterCondition[];
};

export const TeamPage = (): JSX.Element => {
  const dispatch = useAppDispatch();

  // Redux state
  const ownId = useAppSelector((state) => state.settings.profile.profile?.id);
  const favorites = useAppSelector(
    (state) => new Set<string>(state.settings.personal.favouriteTeamMemberIds),
  );
  const isMicrophoneBlocked = useAppSelector(
    (state) => state.telephony.isMicrophoneBlocked,
  );

  // Redux actions
  const personalAddToFavouriteTeamMemberIds = useCallback(
    (payload: { agentId: string }) =>
      bindActionToPromise(
        dispatch,
        personalActions.personalAddToFavouriteTeamMemberIds,
      )(payload),
    [dispatch],
  );

  const personalRemoveFromFavouriteTeamMemberIds = useCallback(
    (payload: { agentId: string }) =>
      bindActionToPromise(
        dispatch,
        personalActions.personalRemoveFromFavouriteTeamMemberIds,
      )(payload),
    [dispatch],
  );

  const showUserModal = useCallback(
    (user: Partial<User>) => dispatch(userModalActions.show({ user })),
    [dispatch],
  );

  const twilioMakeInternalCall = useCallback(
    (userId: string) =>
      bindActionToPromise(
        dispatch,
        twilioActions.twilioMakeInternalCall,
      )(userId),
    [dispatch],
  );
  const hasConversationReplyAccess = useHasAccess('conversation.reply');
  const hasOrganizationMemberAccess = useHasAccess('organization.member');
  const hasPresenceAccess = useHasAccess(
    [
      'organization.member.presence.status',
      'organization.member.presence.working_media',
    ],
    { strategy: 'any' },
  );

  const flags = useFeatureFlag(['custom-away-status']);

  const [expandedMemberId, setExpandedMemberId] = useState<string | null>(null);
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const { params, setParams } = useAppSearchParams('/team');

  // Parse URL state
  const page = useMemo(() => {
    const pageParam = Number(params.page);
    return isNaN(pageParam) || pageParam < 1 ? 1 : pageParam;
  }, [params.page]);

  const activeFilters: ActiveFilters = useMemo(() => {
    const statusParam = params.status ?? null;
    // Normalize arrays - useAppSearchParams may return single values or arrays
    const teamParam = params.team;
    const agentParam = params.agent;

    return {
      status: isValidStatus(statusParam) ? statusParam : null,
      team: Array.isArray(teamParam) ? teamParam : teamParam ? [teamParam] : [],
      agent: Array.isArray(agentParam)
        ? agentParam
        : agentParam
          ? [agentParam]
          : [],
    };
  }, [params.status, params.team, params.agent]);

  // Update URL state
  const setPage = useCallback(
    (newPage: number) => {
      setParams({ page: newPage.toString() });
    },
    [setParams],
  );

  const setActiveFilters = useCallback(
    (updater: ActiveFilters | ((prev: ActiveFilters) => ActiveFilters)) => {
      const newFilters =
        typeof updater === 'function' ? updater(activeFilters) : updater;

      // Reset to page 1 when filters change
      setParams({
        page: '1',
        status: newFilters.status ?? undefined,
        team: newFilters.team.length > 0 ? newFilters.team : undefined,
        agent: newFilters.agent.length > 0 ? newFilters.agent : undefined,
      });
    },
    [activeFilters, setParams],
  );

  const clearFilters = useCallback(() => {
    setParams({
      page: '1',
      status: undefined,
      team: undefined,
      agent: undefined,
    });
  }, [setParams]);

  const availableFilters: AvailableFilters = useMemo(
    () => ({
      /* STATUS FILTERS */

      status: {
        working: [
          {
            field: 'is_working',
            operator: 'equals',
            values: [{ label: '', value: 'true' }],
          },
          {
            field: 'is_online',
            operator: 'equals',
            values: [{ label: '', value: 'true' }],
          },
        ],
        away: [
          {
            field: 'is_working',
            operator: 'equals',
            values: [{ label: '', value: 'false' }],
          },
          {
            field: 'is_online',
            operator: 'equals',
            values: [{ label: '', value: 'true' }],
          },
        ],
        online: [
          {
            field: 'is_online',
            operator: 'equals',
            values: [{ label: '', value: 'true' }],
          },
        ],
        offline: [
          {
            field: 'is_online',
            operator: 'not_equals',
            values: [{ label: '', value: 'true' }],
          },
        ],
        'wrap-up': [
          {
            field: 'has_acw',
            operator: 'equals',
            values: [{ label: '', value: 'true' }],
          },
        ],
        favorite: [
          {
            field: 'id',
            operator: 'is_one',
            values: Array.from(favorites).map((id) => ({
              label: '',
              value: id,
            })),
          },
        ],
        assigned: [
          {
            field: 'is_assigned',
            operator: 'equals',
            values: [{ label: '', value: 'true' }],
          },
        ],
        unassigned: [
          {
            field: 'is_assigned',
            operator: 'not_equals',
            values: [{ label: '', value: 'true' }],
          },
        ],
      },

      /** TEAM FILTERS */

      team: (teamIds: string[]) => [
        {
          field: 'teams',
          operator: 'is_one',
          values: teamIds.map((teamId) => ({ label: '', value: teamId })),
        } satisfies SearchFilterCondition,
      ],

      /** AGENT FILTERS */

      agent: (agentIds) => [
        {
          field: 'id',
          operator: 'is_one',
          values: agentIds.map((agentId) => ({ label: '', value: agentId })),
        } satisfies SearchFilterCondition,
      ],
    }),
    [favorites],
  );

  const queryConditions: SearchFilterCondition[] = useMemo(
    () => [
      ...(activeFilters.status
        ? availableFilters.status[activeFilters.status]
        : ([] as SearchFilterCondition[])),
      ...(activeFilters.team.length
        ? availableFilters.team(activeFilters.team)
        : []),
      ...(activeFilters.agent.length
        ? availableFilters.agent(activeFilters.agent)
        : []),
    ],
    [activeFilters, availableFilters],
  );

  const searchQueryParams: ReadAdvancedSearchResultsApiArg = useMemo(
    () => ({
      params: {
        scope: 'contacts',
        query: '',
        filters: {
          strategy: 'all',
          conditions: [
            {
              field: 'user_type',
              operator: 'is_one',
              values: [{ label: '', value: 'member' }],
            },
            ...queryConditions,
          ],
        },
        sort: { field: 'display_name', order: 'asc' },
        enrichmentSpec: ['workload_config'],
        strict: true,
        page,
      },
    }),
    [page, queryConditions],
  );

  const { members, memberCount, loading, pageCount } =
    searchApi.useReadAdvancedSearchResultsQuery(searchQueryParams, {
      selectFromResult: ({ data, isLoading, isFetching }) => {
        const filteredResult = (data?.data.results || []).filter(
          (item) => item.hits_type === 'user',
        );
        const agentsResult =
          filteredResult.length > 0 ? filteredResult[0] : null;

        return {
          members: agentsResult ? agentsResult.hits : [],
          memberCount: agentsResult?.nb_hits,
          loading: isLoading || isFetching,
          pageCount: agentsResult?.nb_pages,
        };
      },
    });

  useMemberEventsCacheSync(searchQueryParams);

  // Subscribe to organization presence events to receive member updates
  useEffect(() => {
    dispatch(
      websocketActions.request({
        type: 'connection:subscribe',
        params: {
          type: 'organization_presence',
        },
      }),
    );

    return () => {
      dispatch(
        websocketActions.request({
          type: 'connection:unsubscribe',
          params: {
            type: 'organization_presence',
          },
        }),
      );
    };
  }, [dispatch]);

  useScrollToTopOnPageChange(page, loading, members, tableWrapperRef);

  const { conversations, loading: conversationsLoading } =
    conversationsApi.useReadConversationsQuery(
      {
        agentIds: expandedMemberId ? [expandedMemberId] : [],
        status: ['open'],
      },
      {
        skip: !expandedMemberId,
        selectFromResult: ({ data, isLoading, isFetching }) => {
          return {
            conversations: data?.data.conversations,
            loading: isLoading || isFetching,
          };
        },
      },
    );

  const { awayStatuses } = presenceApi.useReadAwayStatusesQuery(
    { isDeactivated: false },
    {
      skip: !flags.customAwayStatus,
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        awayStatuses: data?.data.customAwayStatuses ?? [],
      }),
    },
  );

  const toggleFavorite = useCallback(
    (isFavorite: boolean, agentId: string) => async () => {
      ampli.teamFavoriteToggled({ value: !isFavorite });
      try {
        if (isFavorite) {
          await personalRemoveFromFavouriteTeamMemberIds({ agentId });
        } else {
          await personalAddToFavouriteTeamMemberIds({ agentId });
        }
      } catch {
        // Silently ignore errors
      }
    },
    [
      personalAddToFavouriteTeamMemberIds,
      personalRemoveFromFavouriteTeamMemberIds,
    ],
  );

  const toggleRowExpanded = useCallback(
    (memberId: string) => (e: MouseEvent<HTMLElement>) => {
      const clickedOnDropdownButton = !!(e.target as HTMLElement).closest(
        '[data-dropdown-button="true"]',
      );
      if (clickedOnDropdownButton) {
        return;
      }
      const open = expandedMemberId !== memberId;

      open ? ampli.teamAgentExpanded() : ampli.teamAgentCollapsed();
      open ? setExpandedMemberId(memberId) : setExpandedMemberId(null);
    },
    [expandedMemberId],
  );

  return (
    <Page>
      <Page.Head
        title={
          <FlexBox gap="200">
            Team
            {memberCount != null && (
              <Badge size="medium" variant="secondary">
                {memberCount}
              </Badge>
            )}
          </FlexBox>
        }
        controls={
          <SearchControls
            activeFilters={activeFilters}
            setActiveFilters={setActiveFilters}
          />
        }
      />
      <Page.Body>
        {!loading && members.length === 0 && hasActiveFilters(activeFilters) ? (
          <TeamTableEmptyState onClearFilters={clearFilters} />
        ) : (
          <FlexBox flexDirection="column" className={styles.wrapper}>
            <div ref={tableWrapperRef} className={styles.memberList}>
              {loading && members.length === 0 ? (
                <TeamTableLoading />
              ) : (
                members.map((member) => {
                  const isFavorite = favorites.has(member.id);
                  const isOwnAgent = ownId === member.id;

                  return (
                    <TeamTableRow
                      key={member.id}
                      member={member}
                      hasPresenceAccess={hasPresenceAccess}
                      isExpanded={expandedMemberId === member.id}
                      hasOrganizationMemberAccess={hasOrganizationMemberAccess}
                      hasConversationReplyAccess={hasConversationReplyAccess}
                      isMicrophoneBlocked={isMicrophoneBlocked}
                      twilioMakeInternalCall={twilioMakeInternalCall}
                      showUserModal={showUserModal}
                      onToggle={toggleRowExpanded(member.id)}
                      isFavorite={isFavorite}
                      toggleFavorite={toggleFavorite(isFavorite, member.id)}
                      isOwnAgent={isOwnAgent}
                      conversations={conversations || []}
                      conversationsLoading={conversationsLoading}
                      awayStatuses={awayStatuses}
                    />
                  );
                })
              )}
            </div>
            {pageCount && pageCount > 1 && (
              <FlexBox
                alignItems="center"
                gap="200"
                padding="300"
                className={styles.footer}
              >
                <Pagination
                  currentPage={page}
                  pageCount={pageCount ?? 1}
                  onPageChange={setPage}
                />
                {loading && <Spinner size={16} />}
              </FlexBox>
            )}
          </FlexBox>
        )}
      </Page.Body>
    </Page>
  );
};
